'use client'

import { CustomDataSaveButtons } from '@repo/custom-data-pages/CustomDataSaveButtons'
import { DateSelection } from '@repo/ui/DateSelection'
import { extractClientMatterNumbers } from '@repo/utils/clientMatterNumbers'
import { FeeEarnerSelect } from '@repo/ui/FeeEarnerSelect'
import { formatDate, getUtcDate } from '@repo/utils/time'
import { KeyDateLocationSelect } from '@repo/ui/KeyDateLocationSelect'
import { KeyDateTypeSelect } from '@repo/ui/KeyDateTypeSelect'
import { MatterSelect } from '@repo/ui/MatterSelect'
import { minutesToTimePickerValue, getMinutesFrom24hTime } from '@repo/utils/time'
import { TextEntry } from '@repo/ui/TextEntry'
import { TimePicker } from '@repo/ui/TimePicker'
import { useAuthenticationStore } from '@repo/store/useAuthenticationStore'
import { useFeeEarnerStore } from '@repo/store/useFeeEarnerStore'
import { useKeyDateStore } from '@repo/store/useKeyDateStore'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { ValidationErrorAlert } from '@repo/ui/ValidationErrorAlert'
import Cookies from 'js-cookie'
import MUIAlert from '@mui/material/Alert'
import MUICheckbox from '@mui/material/Checkbox'
import MUIFormControlLabel from '@mui/material/FormControlLabel'
import MUIRadio from '@mui/material/Radio'
import MUIRadioGroup from '@mui/material/RadioGroup'
import MUIStack from '@mui/material/Stack'
import MUISwitch from '@mui/material/Switch'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'

export const KeyDateForm = ({ keyDate = null, testData = null }) => {
  const { userId: loggedInUserId, feeEarnerId: loggedInFeeEarnerId, getFreshOsprey1Cookies } = useAuthenticationStore()
  const {
    createKeyDate,
    updateKeyDate,
    toggleKeyDatesDrawer,
    keyDateTypes,
    keyDateLocationsById,
    fetchKeyDateTypeById
  } = useKeyDateStore()
  const { feeEarnersById } = useFeeEarnerStore()
  const { matters, currentClientMatterNo, matterSearchResults } = useMattersStore()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [validationMessage, setValidationMessage] = useState(null)
  const [selectedKeyDateType, setSelectedKeyDateType] = useState({})
  const [selectedKeyDateLocation, setSelectedKeyDateLocation] = useState({})
  const [selectedFeeEarner, setSelectedFeeEarner] = useState({})
  const [selectedMatter, setSelectedMatter] = useState({})
  const [selectedStartTime, setSelectedStartTime] = useState(null)
  const [selectedEndTime, setSelectedEndTime] = useState(null)
  const defaultDate = formatDate(new Date(), 'yyyy-MM-dd')
  const mode = keyDate ? 'edit' : 'add'

  // there is a cookie that contains the ms token for the logged in user
  // this is used to sync the key date to outlook

  const getMsAccessToken = () => {
    return Cookies.get(`${loggedInUserId}MsToken`) || null
  }

  const msAccessToken = getMsAccessToken()

  const { clientNo: currentClientNo, matterNo: currentMatterNo } = extractClientMatterNumbers({
    clientMatterNo: currentClientMatterNo
  }) || { clientNo: null, matterNo: null }

  const [formData, setFormData] = useState({
    keyDateTypeId: keyDate?.keyDateTypeId || null,
    clientNo: keyDate?.clientNo || currentClientNo,
    matterNo: keyDate?.matterNo || currentMatterNo,
    date: keyDate?.date || defaultDate,
    notes: keyDate?.notes || null,
    dateMet: keyDate?.dateMet || false,
    feeEarnerId: keyDate?.feeEarnerId || loggedInFeeEarnerId || null,
    taskId: keyDate?.taskId || 0,
    userId: keyDate?.userId || loggedInUserId,
    published: keyDate?.published || false,
    outlookEventId: keyDate?.outlookEventId || null,
    mustAttend: keyDate?.mustAttend === true ? true : keyDate?.mustAttend === false ? false : null,
    startTime: keyDate?.startTime || null,
    isAllDay: mode === 'add' ? true : keyDate?.isAllDay,
    durationMinutes: keyDate?.durationMinutes || null,
    criminalNotes: keyDate?.criminalNotes || null,
    keyDateLocationId: keyDate?.keyDateLocationId || null,
    synchronizeOutlookEvent: mode === 'add' ? Boolean(msAccessToken) : Boolean(keyDate.outlookEventId),
    accessToken: msAccessToken
  })

  useEffect(() => {
    if (!formData.keyDateTypeId || !keyDateTypes) {
      setSelectedKeyDateType(null)
      return
    }

    let keyDateType = keyDateTypes[formData.keyDateTypeId] || null

    if (keyDateType) {
      setSelectedKeyDateType(keyDateType)
      return
    }

    const fetchAndSetKeyDateType = async () => {
      try {
        const keyDateType = await fetchKeyDateTypeById({ keyDateTypeId: formData.keyDateTypeId })
        if (keyDateType) {
          setSelectedKeyDateType(keyDateType)
        } else {
          setSelectedKeyDateType(null)
        }
      } catch (error) {
        console.error(error)
        setSelectedKeyDateType(null)
      }
    }

    fetchAndSetKeyDateType()
  }, [formData.keyDateTypeId, keyDateTypes, fetchKeyDateTypeById])

  useEffect(() => {
    if (formData.keyDateLocationId) {
      const keyDateLocation = keyDateLocationsById[formData.keyDateLocationId]
      setSelectedKeyDateLocation(keyDateLocation)
    } else {
      setSelectedKeyDateLocation(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.keyDateLocationId])

  useEffect(() => {
    if (formData.clientNo) {
      const clientMatterNo = `${formData.clientNo}-${formData.matterNo}`
      let matter = matters[clientMatterNo]

      if (!matter) {
        matter = matterSearchResults.find((result) => result.clientMatterNo === clientMatterNo)
      }

      if (matter) setSelectedMatter(matter)
      else setSelectedMatter(null)
    } else {
      setSelectedMatter(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.clientNo, formData.matterNo])

  useEffect(() => {
    if (formData.feeEarnerId) {
      const feeEarner = feeEarnersById[formData.feeEarnerId]
      setSelectedFeeEarner(feeEarner)
    } else {
      setSelectedFeeEarner(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.feeEarnerId])

  useEffect(() => {
    if (formData.isAllDay) {
      // if all day, clear start time and duration minutes
      handleInputChange('startTime', null)
      handleInputChange('durationMinutes', null)
    }
  }, [formData.isAllDay])

  useEffect(() => {
    const startTimeInMinutes = getMinutesFrom24hTime(formData.startTime)

    if (formData.startTime) {
      const startTimeInTimePickerValueFormat = minutesToTimePickerValue(startTimeInMinutes)
      setSelectedStartTime(startTimeInTimePickerValueFormat)
    } else {
      setSelectedStartTime(null)
    }

    if (formData.startTime && formData.durationMinutes) {
      const endTimeInMinutes = startTimeInMinutes + formData.durationMinutes
      const endTimeInTimePickerValueFormat = minutesToTimePickerValue(endTimeInMinutes)
      setSelectedEndTime(endTimeInTimePickerValueFormat)
    } else {
      setSelectedEndTime(null)
    }
  }, [formData.durationMinutes, formData.startTime])

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }))

    setTimeout(() => {}, 200)
  }

  const handleMatterChange = (value) => {
    const { clientNo, matterNo } = value || { clientNo: null, matterNo: null }
    handleInputChange('clientNo', clientNo)
    handleInputChange('matterNo', matterNo)
  }

  const handleStartTimeChange = (arg) => {
    if (!arg) {
      setSelectedStartTime(null)
      handleInputChange('startTime', null)
      handleInputChange('durationMinutes', null)
      return
    }

    const { value } = arg
    const startTimeInMinutes = getMinutesFrom24hTime(value)
    const startTimeInTimePickerValueFormat = minutesToTimePickerValue(startTimeInMinutes)
    setSelectedStartTime(startTimeInTimePickerValueFormat)
    handleInputChange('startTime', value)
  }

  const handleEndTimeChange = (arg) => {
    if (!arg) {
      setSelectedEndTime(null)
      handleInputChange('durationMinutes', null)
      return
    }

    const { value } = arg
    const startTimeInMinutes = getMinutesFrom24hTime(selectedStartTime.value)
    const endTimeInMinutes = getMinutesFrom24hTime(value)
    const durationMinutes = endTimeInMinutes - startTimeInMinutes
    handleInputChange('durationMinutes', durationMinutes)
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.notes?.trim()) {
      newErrors.description = 'Key Date Description is required'
    }

    if (!formData.date) {
      newErrors.date = 'Date is required'
    }

    if (!formData.clientNo?.trim()) {
      newErrors.matter = 'Associated Matter is required'
    }

    if (!formData.keyDateTypeId?.trim()) {
      newErrors.keyDateType = 'Key Date Type is required'
    }

    if (!formData.feeEarnerId?.trim()) {
      newErrors.feeEarnerId = 'Fee Earner is required'
    }

    setErrors(newErrors)
    if (Object.entries(newErrors).length) console.log(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    console.log(formData)

    if (!validateForm()) {
      setValidationMessage(null)
      return
    }

    setIsLoading(true)

    let keyDateId = keyDate?.keyDateId || null

    const payload = { ...formData }

    if (msAccessToken) {
      // We are calling this function to trigger an http event that will force Osprey 1 to send the latest msAccessToken cookie to the browser
      await getFreshOsprey1Cookies()

      // make sure we have the latest msAccessToken value, as it might have been refreshed since the form was rendered
      const accessToken = getMsAccessToken()
      if (accessToken) payload.accessToken = accessToken
    }

    try {
      if (mode === 'edit') {
        await updateKeyDate({ ...payload, keyDateId })
      } else {
        keyDateId = await createKeyDate({ ...payload })
      }

      onClose(keyDateId)
    } catch (error) {
      console.error('Error submitting key date:', error)
      setErrors({ submit: 'Failed to save contact. Please try again.' })

      const { message } = error?.response?.data || {}
      setValidationMessage(message || null)
    } finally {
      setIsLoading(false)
    }
  }

  const handleOutlookSetupClick = () => {
    navigate('/configuration/microsoft/outlook-setup')
    onClose()
  }

  const onClose = (keyDateId) => {
    toggleKeyDatesDrawer({
      keyDatesDrawerIsOpen: Boolean(keyDateId),
      keyDatesDrawerItemId: keyDateId || null,
      keyDatesDrawerMode: 'view'
    })
  }

  return (
    <form onSubmit={handleSubmit} data-testid="KeyDateForm" style={{ width: '100%' }}>
      <MUIStack
        direction="column"
        gap={2}
        sx={{
          width: 'calc(100% - 40px)',
          backgroundColor: '#DFD1B655',
          padding: '20px 20px 30px',
          marginBottom: '20px',
          marginLeft: '20px',
          marginRight: '20px',
          borderRadius: '6px'
        }}
      >
        <MUIStack direction="column" gap={4} flexWrap="wrap">
          <MUITypography variant="h6" sx={{ textTransform: 'uppercase', mb: 0, mt: 2 }}>
            Key date
          </MUITypography>

          <TextEntry
            name="description"
            label="Key date description"
            value={formData.notes || ''}
            onChange={(value) => handleInputChange('notes', value)}
            size="small"
            sx={{ flex: 1, backgroundColor: 'white', minWidth: '240px' }}
            limit={50}
            errors={errors}
            required
          />

          <MUIFormControlLabel
            sx={{ mb: 5, mt: -2, ml: -1 }}
            control={
              <MUISwitch
                checked={formData.dateMet}
                onChange={(e) => handleInputChange('dateMet', e.target.checked)}
                color="secondary"
                sx={{
                  padding: '6px 6px',
                  '& .MuiSwitch-thumb': {
                    backgroundColor: 'white'
                  },
                  '& .MuiSwitch-track': {
                    backgroundColor: 'secondary.main',
                    opacity: `${formData.dateMet ? 1 : 0.3} !important`,
                    borderRadius: '12px'
                  }
                }}
              />
            }
            label="Date met"
          />

          <MUITypography variant="h6" sx={{ textTransform: 'uppercase', mb: 0, mt: 2 }}>
            Date / time
          </MUITypography>

          <MUIStack direction="row" gap={6} justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
            <DateSelection
              value={formData.date ? getUtcDate(formData.date.split('T')[0]) : getUtcDate()}
              onChange={(value) =>
                handleInputChange('date', value ? formatDate(value, 'yyyy-MM-dd') : null)
              }
              sx={{ width: '240px', mb: 0 }}
              label="Date"
              required
              name="date"
              errors={errors}
              clearable={false}
              enableTypedDdMmYyyy={true}
              showAdornment={true}
            />

            <MUIFormControlLabel
              sx={{ flex: 1 }}
              control={
                <MUICheckbox
                  checked={formData.synchronizeOutlookEvent}
                  onChange={(e) => handleInputChange('synchronizeOutlookEvent', e.target.checked)}
                  disabled={!msAccessToken || (mode === 'edit' && Boolean(formData.outlookEventId))}
                />
              }
              label="Sync to Outlook"
            />
          </MUIStack>

          {!msAccessToken ? (
            <MUIAlert
              severity="info"
              sx={{
                width: '100%',
                color: 'white',
                '&.MuiAlert-colorInfo': { backgroundColor: 'secondary.light' },
                '.MuiAlert-icon': { opacity: 1, 'svg path': { fill: 'white' } }
              }}
            >
              You cannot sync Key Dates to Outlook until you{' '}
              <a
                style={{ color: 'white', textDecoration: 'underline', cursor: 'pointer' }}
                onClick={handleOutlookSetupClick}
              >
                set up an integration.
              </a>
            </MUIAlert>
          ) : null}

          <MUIRadioGroup
            name="isAllDay"
            value={formData.isAllDay === true ? 'isAllDay' : formData.isAllDay === false ? 'isTime' : null}
            onChange={(e) =>
              handleInputChange(
                'isAllDay',
                e.target.value === 'isAllDay' ? true : e.target.value === 'isTime' ? false : null
              )
            }
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-start',
              mt: -2,
              mb: 0,
              width: '100%'
            }}
          >
            <MUIFormControlLabel value={'isAllDay'} control={<MUIRadio />} label="All day" sx={{ mr: 10 }} />
            <MUIFormControlLabel value={'isTime'} control={<MUIRadio />} label="Set time" />
          </MUIRadioGroup>

          {formData.isAllDay === false ? (
            <MUIStack direction="row" gap={2} justifyContent="flex-start" alignItems="center" sx={{ width: '100%' }}>
              <TimePicker
                timeStep={30}
                sx={{ flex: 1 }}
                menuZindex={9999999}
                onChange={handleStartTimeChange}
                value={selectedStartTime}
                label="Start time"
                allowTyping
              />

              <TimePicker
                timeStep={30}
                sx={{ flex: 1 }}
                menuZindex={9999999}
                onChange={handleEndTimeChange}
                value={selectedEndTime}
                label="End time"
                disabled={!formData.startTime}
                startAt={selectedStartTime?.value || null}
                allowTyping
              />
            </MUIStack>
          ) : null}

          <MUITypography variant="h6" sx={{ textTransform: 'uppercase', mt: 6, mb: 0, width: '100%' }}>
            Key date details
          </MUITypography>

          <MatterSelect
            value={selectedMatter}
            onChange={handleMatterChange}
            sx={{ width: '100%' }}
            menuZindex={9999999}
            label="Associated matter"
            errors={errors}
            name="matter"
            required
          />

          <KeyDateTypeSelect
            value={selectedKeyDateType}
            onChange={(value) => handleInputChange('keyDateTypeId', value ? value.keyDateTypeId : null)}
            sx={{ width: '100%', mb: 2 }}
            name="keyDateType"
            errors={errors}
            testData={testData}
            required
          />

          <FeeEarnerSelect
            value={selectedFeeEarner}
            onChange={(value) => handleInputChange('feeEarnerId', value ? value.feeEarnerId : null)}
            sx={{ width: '100%', mb: 2 }}
            menuZindex={9999999}
            label="Key date fee earner"
            errors={errors}
            name="feeEarnerId"
            testData={testData}
            required
          />

          <MUIFormControlLabel
            sx={{ mb: 3, mt: -2 }}
            control={
              <MUICheckbox
                checked={formData.published}
                onChange={(e) => handleInputChange('published', e.target.checked)}
              />
            }
            label="Publish to client portal"
          />

          <MUITypography variant="h6" sx={{ textTransform: 'uppercase', mb: 0, mt: 4 }}>
            Attendance
          </MUITypography>
          <MUIRadioGroup
            name="mustAttend"
            value={formData.mustAttend === true ? 'mustAttend' : formData.mustAttend === false ? 'optional' : null}
            onChange={(e) =>
              handleInputChange(
                'mustAttend',
                e.target.value === 'mustAttend' ? true : e.target.value === 'optional' ? false : null
              )
            }
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-start',
              mt: -2,
              mb: 0,
              width: '100%'
            }}
          >
            <MUIFormControlLabel value={'mustAttend'} control={<MUIRadio />} label="Must attend" sx={{ mr: 10 }} />
            <MUIFormControlLabel value={'optional'} control={<MUIRadio />} label="Optional" sx={{ mr: 10 }} />
            <MUIFormControlLabel value={null} control={<MUIRadio />} label="N/A" />
          </MUIRadioGroup>

          <KeyDateLocationSelect
            value={selectedKeyDateLocation}
            onChange={(value) => handleInputChange('keyDateLocationId', value ? value.keyDateLocationId : null)}
            sx={{ width: '100%', mb: 2 }}
            testData={testData}
          />

          <MUITypography variant="h6" sx={{ textTransform: 'uppercase', mb: 0, mt: 8 }}>
            Additional details
          </MUITypography>
          <TextEntry
            name="criminalNotes"
            label=""
            value={formData.criminalNotes || ''}
            onChange={(value) => handleInputChange('criminalNotes', value)}
            size="small"
            sx={{ flex: 1, backgroundColor: 'white', minWidth: '240px' }}
            limit={5000}
            errors={errors}
            multiline
            placeholder="Store helpful details such as URN, DSCC, Client DOB etc."
            rows={10}
          />
        </MUIStack>

        <ValidationErrorAlert errorCount={Object.keys(errors).length} validationMessage={validationMessage} />

        <MUIStack direction="row" justifyContent="flex-end" gap={2}>
          <CustomDataSaveButtons
            onSave={handleSubmit}
            onCancel={() => (mode === 'add' ? onClose() : onClose(keyDate.keyDateId))}
            sx={{ width: '200px' }}
            disabled={isLoading}
            type="submit"
          />
        </MUIStack>
      </MUIStack>
    </form>
  )
}

KeyDateForm.propTypes = {
  keyDate: PropTypes.object,
  testData: PropTypes.any
}
